import { sendNotificationEmail } from '../LeaveRoster';
// ============================================================
// CreditQueryCard.jsx
// Shared component used in both CAS Dashboard modal and CMS AssessmentPage
// Props:
//   mcRow, outletId, outletName
//   role: 'sales' | 'approver'
//   senderName, senderEmail
//   recipientEmail — who gets the notification email
//   lang: 'en' | 'bm'
//   onQuerySent — callback after message sent
// ============================================================

import React, { useState, useEffect, useRef } from 'react';

const T = {
  en: {
    title:          'Credit Query',
    noQuery:        'No credit query initiated.',
    placeholder:    'Type your message...',
    send:           'Send',
    sending:        'Sending...',
    startQuery:     'Initiate Credit Query',
    reply:          'Reply',
    uploadFile:     'Attach File',
    fileAttached:   'File attached',
    removeFile:     'Remove',
    error:          'Failed to send. Please try again.',
    uploadError:    'File upload failed.',
  },
  bm: {
    title:          'Credit Query',
    noQuery:        'Tiada credit query dimulakan.',
    placeholder:    'Taip mesej anda...',
    send:           'Hantar',
    sending:        'Menghantar...',
    startQuery:     'Mulakan Credit Query',
    reply:          'Balas',
    uploadFile:     'Lampirkan Fail',
    fileAttached:   'Fail dilampirkan',
    removeFile:     'Buang',
    error:          'Gagal menghantar. Sila cuba lagi.',
    uploadError:    'Muat naik fail gagal.',
  },
};

const s = {
  card:      { background: '#fff', borderRadius: 10, padding: '14px 16px', marginBottom: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '0.5px solid #e0e0e0' },
  title:     { fontSize: 12, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#555', marginBottom: 12, paddingBottom: 6, borderBottom: '0.5px solid #f0f0f0' },
  messages:  { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 },
  msgWrap:   (role) => ({
    display: 'flex', flexDirection: 'column',
    alignItems: role === 'approver' ? 'flex-end' : 'flex-start',
  }),
  msgBubble: (role) => ({
    maxWidth: '85%', padding: '8px 12px', borderRadius: 10,
    background: role === 'approver' ? '#c8102e' : '#f0f2f5',
    color: role === 'approver' ? '#fff' : '#1a1a2e',
    fontSize: 13, lineHeight: 1.5,
  }),
  msgMeta:   { fontSize: 10, color: '#aaa', marginTop: 3 },
  fileLink:  (role) => ({
    fontSize: 11, marginTop: 4,
    color: role === 'approver' ? 'rgba(255,255,255,0.8)' : '#1967d2',
    textDecoration: 'underline', cursor: 'pointer',
  }),
  inputArea: { borderTop: '0.5px solid #f0f0f0', paddingTop: 12 },
  textarea:  { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, resize: 'none', minHeight: 72, boxSizing: 'border-box', fontFamily: 'inherit' },
  actions:   { display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' },
  sendBtn:   { padding: '8px 18px', background: '#c8102e', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  sendBtnDis:{ opacity: 0.4, cursor: 'not-allowed' },
  uploadBtn: { padding: '8px 12px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 8, fontSize: 12, cursor: 'pointer' },
  fileTag:   { fontSize: 11, color: '#555', display: 'flex', alignItems: 'center', gap: 6 },
  removeBtn: { fontSize: 11, color: '#c5221f', cursor: 'pointer', background: 'none', border: 'none', padding: 0 },
  initBtn:   { width: '100%', padding: '10px 0', background: '#fff', border: '1px solid #c8102e', color: '#c8102e', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 },
  empty:     { fontSize: 13, color: '#aaa', fontStyle: 'italic' },
  err:       { fontSize: 12, color: '#c5221f', marginTop: 6 },
};

export default function CreditQueryCard({
  mcRow, outletId, outletName,
  role, senderName, senderEmail,
  recipientEmail, lang = 'en',
  onQuerySent,
}) {
  const t = T[lang] || T.en;
  const fileRef = useRef();

  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [msg,      setMsg]      = useState('');
  const [file,     setFile]     = useState(null);
  const [sending,  setSending]  = useState(false);
  const [err,      setErr]      = useState('');
  const [showInput, setShowInput] = useState(false);

  useEffect(() => {
    if (!mcRow) return;
    setLoading(true);
    fetch(`/api/credit-query/${mcRow}`)
      .then(r => r.json())
      .then(d => { setMessages(d.messages || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [mcRow]);

  const lastMsg    = messages[messages.length - 1];
  const hasQuery   = messages.length > 0;
  // Show reply input if: showInput triggered OR it's sales turn (last msg from approver) OR approver turn (last msg from sales)
  const isSalesTurn    = hasQuery && lastMsg?.role === 'approver';
  const isApproverTurn = hasQuery && lastMsg?.role === 'sales';
  const canReply = (role === 'sales' && isSalesTurn) || (role === 'approver' && isApproverTurn);

  async function handleSend() {
    if (!msg.trim()) return;
    setSending(true); setErr('');
    try {
      let fileUrl = null;

      // Upload file first if attached (sales only)
      if (file && role === 'sales') {
        const toBase64 = f => new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result.split(',')[1]);
          r.onerror = rej;
          r.readAsDataURL(f);
        });
        const data = await toBase64(file);
        const upRes = await fetch(`/api/credit-query/${mcRow}/upload`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: file.name, mimeType: file.type, data, outletId, outletName }),
        });
        const upData = await upRes.json();
        if (!upData.success) throw new Error(t.uploadError);
        fileUrl = upData.fileUrl;
      }

      const res = await fetch(`/api/credit-query/${mcRow}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role, senderName, senderEmail,
          message: msg.trim(), fileUrl,
          recipientEmail, outletId,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      if (data.notify) sendNotificationEmail({ ...data.notify, iosBanner: role === 'sales' });
      setMessages(data.messages);
      setMsg(''); setFile(null); setShowInput(false);
      if (onQuerySent) onQuerySent(data.messages);
    } catch (e) {
      setErr(e.message || t.error);
    }
    setSending(false);
  }

  if (loading) return (
    <div style={s.card}>
      <div style={s.title}>{t.title}</div>
      <div style={s.empty}>Loading...</div>
    </div>
  );

  return (
    <div style={s.card}>
      <div style={s.title}>{t.title}</div>

      {/* Conversation */}
      {messages.length === 0 ? (
        <div style={s.empty}>{t.noQuery}</div>
      ) : (
        <div style={s.messages}>
          {messages.map((m, i) => (
            <div key={i} style={s.msgWrap(m.role)}>
              <div style={s.msgBubble(m.role)}>{m.msg}</div>
              {m.fileUrl && (
                <a href={m.fileUrl} target="_blank" rel="noreferrer" style={s.fileLink(m.role)}>
                  📎 {lang === 'en' ? 'View attachment' : 'Lihat lampiran'}
                </a>
              )}
              <div style={s.msgMeta}>{m.name} · {m.ts}</div>
            </div>
          ))}
        </div>
      )}

      {/* Input area */}
      {/* Approver: show Initiate button if no query yet */}
      {role === 'approver' && !hasQuery && (
        <button style={s.initBtn} onClick={() => setShowInput(true)}>
          {t.startQuery}
        </button>
      )}

      {/* Show input when: initiating (approver), or it's their turn to reply */}
      {(showInput || canReply) && (
        <div style={s.inputArea}>
          <textarea
            style={s.textarea}
            placeholder={t.placeholder}
            value={msg}
            onChange={e => setMsg(e.target.value)}
          />
          <div style={s.actions}>
            {/* File upload — sales only */}
            {role === 'sales' && (
              <>
                <button style={s.uploadBtn} onClick={() => fileRef.current?.click()}>
                  {t.uploadFile}
                </button>
                <input ref={fileRef} type="file" style={{ display: 'none' }}
                  accept="image/*,.pdf"
                  onChange={e => { setFile(e.target.files[0] || null); e.target.value = ''; }} />
              </>
            )}
            {file && (
              <div style={s.fileTag}>
                <span>📎 {file.name}</span>
                <button style={s.removeBtn} onClick={() => setFile(null)}>{t.removeFile}</button>
              </div>
            )}
            <button
              style={{ ...s.sendBtn, ...(!msg.trim() || sending ? s.sendBtnDis : {}) }}
              disabled={!msg.trim() || sending}
              onClick={handleSend}
            >
              {sending ? t.sending : t.send}
            </button>
          </div>
          {err && <div style={s.err}>{err}</div>}
        </div>
      )}
    </div>
  );
}
